import type { NextPage } from 'next'
import Image from 'next/image'
import Link from 'next/link'
import Script from 'next/script'
import React from 'react'

// import { useScript } from 'usehooks-ts'

// import * as hbspt from 'hbspt-module'
import { gsap } from 'gsap/dist/gsap'
import { ScrollToPlugin } from 'gsap/dist/ScrollToPlugin'
import { useEffect, useRef, useState } from 'react'
import CustomSelect, { Value } from '../../components/customSelect/CustomSelect'
import Select, { Options, SingleValue } from 'react-select'
import CareersSignup from '../../components/careersSignup/CareersSignup'
import Item from '../../components/item/Item'
import ImageQuote from '../../components/imagequote/ImageQuote'
import Grid from '../../components/grid/Grid'
import CustomImage from '../../components/customImage/CustomImage'
import CustomMetaData from '../../components/general/customMetaData/CustomMetaData'
import { GenericPageLayoutProps } from '../../components/general/genericPageLayout/GenericPageLayout'
import { OptionProps, ActionMeta } from 'react-select'
import { useIsMobile } from '../../hooks/useIsDesktop'
import { useGlobalContentContext } from '../../context/GlobalContentContext'
import { parseGenericPage } from '../../utils/parsers'
import { getAPI, createMarkup } from '../../utils/helpers'

import styles from '../../styles/pages/Careers.module.scss'

if (typeof window !== 'undefined') {
  // Due to SSR we can only register when we are in the browser
  gsap.registerPlugin(ScrollToPlugin)
}

type Props = {
  jobOpenings: any
} & GenericPageLayoutProps

declare const hbspt: any

const Careers = (props: Props) => {
  const { setData } = useGlobalContentContext()

  useEffect(() => {
    setData(props.globalData)
  }, [props.globalData, setData])

  const isMobile = useIsMobile()

  const [teamFilter, setTeamFilter] = useState(0)
  const [locationFilter, setLocationFilter] = useState(0)
  const [searchFilter, setSearchFilter] = useState('')

  const [activeDescription, setActiveDescription] = useState(-1)

  const [hbsptForm, setHbsptForm] = useState(false)

  const categories = props.jobOpenings
    .map((opening: any) => opening.categories)
    .flat()
  const allTeams = categories.filter((category: any) => category.key == 'team')
  const teams = allTeams
    .filter((category: any, i: number) => {
      return allTeams.findIndex((cat: any) => cat.id == category.id) == i
    })
    .sort((a: any, b: any) => a.value > b.value)

  const allLocations = categories.filter(
    (category: any) => category.key == 'location'
  )
  const locations = allLocations
    .filter((category: any, i: number) => {
      return allLocations.findIndex((cat: any) => cat.id == category.id) == i
    })
    .sort((a: any, b: any) => a.value > b.value)

  let jobOpenings = props.jobOpenings.sort(
    (a: any, b: any) => a.title > b.title
  )

  const handleTeamChange = (option: SingleValue<Value>) => {
    if (option) {
      setTeamFilter(parseInt(option.value))
    }
  }

  const handleTeamPhoneChange = (e: React.ChangeEvent) => {
    const target = e.target as HTMLInputElement
    if (target.value) {
      setTeamFilter(parseInt(target.value))
    }
  }

  const handleLocationChange = (option: SingleValue<Value>) => {
    if (option) {
      setLocationFilter(parseInt(option.value))
    }
  }
  const handleLocationPhoneChange = (e: React.ChangeEvent) => {
    const target = e.target as HTMLInputElement

    if (target.value) {
      setLocationFilter(parseInt(target.value))
    }
  }

  const goToTeam = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    team: string
  ) => {
    e.preventDefault()
    const teamId = teams.find(
      (cat: any) => cat.value.toLowerCase() == team.toLowerCase()
    ).id
    setTeamFilter(teamId)

    gsap.to(window, { duration: 0.5, scrollTo: '#positions' })
  }

  const teamOptionsValues = teams.map((team: any) => {
    return { value: team.id, label: team.value }
  })

  const teamOptions = [{ value: 0, label: 'All Teams' }, ...teamOptionsValues]

  const locationOptionsValues = locations.map((location: any) => {
    return { value: location.id, label: location.value }
  })
  const locationOptions = [
    { value: 0, label: 'All Locations' },
    ...locationOptionsValues,
  ]

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    e.preventDefault()
    setSearchFilter(e.currentTarget.value.toLowerCase())
  }

  const getBlogTitle = (name: string) => {
    return `${name}'s Story`
  }

  const getJobOpenings = () => {
    return jobOpenings.filter((job: any) => {
      let inTeam = true
      let inLocation = true
      let inSearch = true
      if (teamFilter != 0) {
        inTeam = job.categories.find((cat: any) => cat.id == teamFilter)
      }
      if (locationFilter != 0) {
        inLocation = job.categories.find((cat: any) => cat.id == locationFilter)
      }
      if (searchFilter != '') {
        inSearch = job.title.toLowerCase().includes(searchFilter)
      }
      return inTeam && inLocation && inSearch
    })
  }

  // const status = useScript(`//js.hsforms.net/forms/v2.js`)
  // useEffect(() => {
  //   if (typeof hbspt !== 'undefined') {
  //     hbspt.forms.create({
  //       target: '#hbspt-form-placeholder',
  //       region: 'na1',
  //       portalId: '22091026',
  //       formId: '5883ab40-ab60-44fd-840a-d6f5c9ef603d',
  //     })
  //   }
  // }, [status])

  return (
    <div className={styles.container}>
      <CustomMetaData
        title={props.originalResponse.meta_title}
        description={props.originalResponse.meta_description}
        image={props.originalResponse.meta_image}
      />

      <div className={styles.main}>
        <section className={styles.hero}>
          <div className={styles.heroContentContainer}>
            <h1 className={styles.heroTitle}>{props.originalResponse.title}</h1>
          </div>
          <div className={styles.heroImage}>
            <CustomImage
              src={props.originalResponse.hero}
              layout="fill"
              objectFit="cover"
            />
          </div>
          <div className={styles.heroContentContainer}>
            <h3
              className={styles.heroDetailLarge}
              dangerouslySetInnerHTML={createMarkup(
                props.originalResponse.detail_large
              )}
            ></h3>
            <div
              className={styles.heroDetail}
              dangerouslySetInnerHTML={createMarkup(
                props.originalResponse.detail
              )}
            ></div>
          </div>
        </section>

        <section className={styles.benefitsSection}>
          <h2
            className={styles.benefitsTitle}
            dangerouslySetInnerHTML={createMarkup(
              props.originalResponse.benefitssection.title
            )}
          ></h2>
          <div
            className={styles.benefitsDetail}
            dangerouslySetInnerHTML={createMarkup(
              props.originalResponse.benefitssection.detail
            )}
          ></div>

          <div className={styles.benefitsItems}>
            <Grid columns={3}>
              {props.originalResponse.benefits_items.map(
                (item: any, index: number) => (
                  <div
                    key={index}
                    className={`${styles.benefitsItem} ${
                      item.image && styles.benefitsItemImage
                    }`}
                  >
                    {item.image && (
                      <div className={styles.benefitsIcon}>
                        <CustomImage src={item} layout="responsive" />
                      </div>
                    )}
                    <div className={styles.benefitsItemTitle}>{item.title}</div>
                  </div>
                )
              )}
            </Grid>
          </div>
        </section>

        <section className={styles.peopleSection}>
          <h2 className={styles.peopleSectionTitle}>
            {props.originalResponse.peoplesection.title}
          </h2>
          <div
            className={styles.peopleSectionDetail}
            dangerouslySetInnerHTML={createMarkup(
              props.originalResponse.peoplesection.detail
            )}
          ></div>

          <Grid columns={3}>
            {props.originalResponse.persons.map(
              (person: any, index: number) => (
                <div key={index} className={styles.person}>
                  {person.blog_link ? (
                    <Link href={person.blog_link}>
                      <a>
                        <div className={styles.personImageContainer}>
                          <CustomImage
                            src={person}
                            layout="fill"
                            objectFit="cover"
                          />
                        </div>
                        <h5 className={styles.personName}>{person.name}</h5>
                        <div className={styles.personDetail}>
                          {person.detail}
                        </div>
                        <div className={styles.personLink}>
                          {getBlogTitle(person.name)}
                        </div>
                      </a>
                    </Link>
                  ) : (
                    <>
                      <div className={styles.personImageContainer}>
                        <CustomImage
                          src={person}
                          layout="fill"
                          objectFit="cover"
                        />
                      </div>
                      <h5 className={styles.personName}>{person.name}</h5>
                      <div className={styles.personDetailMargin}>
                        {person.detail}
                      </div>
                    </>
                  )}
                </div>
              )
            )}
          </Grid>

          <div className={styles.peopleSectionList}>
            <Grid columns={3}>
              <div className={styles.peopleSectionBlock}>
                <h5 className={styles.peopleSectionListTitle}>Nationalities</h5>
                <div className={styles.peopleSectionListDetail}>
                  {props.originalResponse.peoplesection.nationalities}
                </div>
              </div>

              <div className={styles.peopleSectionBlock}>
                <h5 className={styles.peopleSectionListTitle}>Employees</h5>
                <div className={styles.peopleSectionListDetail}>
                  {props.originalResponse.peoplesection.employees}
                </div>
              </div>

              <div className={styles.peopleSectionBlock}>
                <h5 className={styles.peopleSectionListTitle}>Locations</h5>
                <div
                  className={styles.peopleSectionListDetailSmall}
                  dangerouslySetInnerHTML={createMarkup(
                    props.originalResponse.peoplesection.locations
                  )}
                ></div>
              </div>
            </Grid>
          </div>
        </section>

        <section className={styles.findYourSpotSection}>
          <h2 className={styles.findYourSpotSectionTitle}>Find your spot</h2>
          <Grid columns={3}>
            {props.originalResponse.yourspots.map(
              (spot: any, index: number) => (
                <React.Fragment key={index}>
                  {teams.findIndex(
                    (cat: any) =>
                      cat.value.toLowerCase() == spot.title.toLowerCase()
                  ) >= 0 ? (
                    <a
                      className={styles.findYourSpotLink}
                      href="#positions"
                      onClick={(e) => goToTeam(e, spot.title)}
                    >
                      <div className={styles.findYourSpot}>
                        <div className={styles.findYourSpotImage}>
                          <CustomImage
                            src={spot}
                            layout="fill"
                            objectFit="cover"
                          />
                          <div className={styles.findYourSpotOverlay}></div>
                        </div>
                        <div className={styles.findYourSpotTitle}>
                          {spot.title}
                        </div>
                        <span className={styles.findYourSpotLinkArrow}>
                          <Image
                            className={styles.linkArrow}
                            src="/arrow-black.svg"
                            layout="fill"
                            alt=""
                          />
                        </span>
                        SEE OUR CURRENT OPENINGS
                      </div>
                    </a>
                  ) : (
                    <div className={styles.findYourSpot}>
                      <div className={styles.findYourSpotImage}>
                        <CustomImage
                          src={spot}
                          layout="fill"
                          objectFit="cover"
                        />
                        <div className={styles.findYourSpotOverlay}></div>
                      </div>
                      <div className={styles.findYourSpotTitle}>
                        {spot.title}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              )
            )}
          </Grid>
        </section>

        <section className={styles.positionsSection} id="positions">
          <h2 className={styles.positionsSectionTitle}>
            {props.originalResponse.currentopeningssection.title}
          </h2>
          <div
            className={styles.positionsSectionDetail}
            dangerouslySetInnerHTML={createMarkup(
              props.originalResponse.currentopeningssection.detail
            )}
          ></div>

          {/* <div className={styles.positionsFilter}>
            <h3 className={styles.positionsFilterTitle}>Filter</h3>

            <div className={styles.positionsFilterSelect}>
              {isMobile ? (
                <select onChange={handleTeamPhoneChange}>
                  {teamOptions.map((teamOption: any, index: number) => (
                    <option key={index} value={teamOption.value}>
                      {teamOption.label}
                    </option>
                  ))}
                </select>
              ) : (
                <CustomSelect
                  id="team-select"
                  onChange={handleTeamChange}
                  options={teamOptions}
                  value={teamOptions.find((team) => team.value == teamFilter)}
                />
              )}
            </div>
            <div className={styles.positionsFilterSelect}>
              {isMobile ? (
                <select onChange={handleLocationPhoneChange}>
                  {locationOptions.map((locationOption: any, index: number) => (
                    <option key={index} value={locationOption.value}>
                      {locationOption.label}
                    </option>
                  ))}
                </select>
              ) : (
                <CustomSelect
                  id="location-select"
                  onChange={handleLocationChange}
                  options={locationOptions}
                  value={locationOptions.find(
                    (location) => location.value == locationFilter
                  )}
                />
              )}
            </div>

            <div className={styles.searchFilter}>
              <label className={styles.searchFilterLabel}>Search</label>
              <input
                type="text"
                placeholder="Job Title"
                onChange={(e) => handleSearch(e)}
              />
            </div>
          </div>

          <div className={styles.positionsOpenings}>
            {getJobOpenings().map((position: any, index: number) => (
              <div key={index} className={styles.position}>
                <h4
                  className={styles.positionTitle}
                  dangerouslySetInnerHTML={createMarkup(
                    position.title.toLowerCase()
                  )}
                ></h4>

                <div className={styles.positionDetail}>
                  <div
                    dangerouslySetInnerHTML={createMarkup(position.detail)}
                  ></div>

                  <div className={styles.positionInformation}>
                    <a
                      href={position.information_link}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <span className={`${styles.arrow} arrow`}>
                        <Image src="/arrow-black.svg" layout="fill" alt="" />
                      </span>
                      Information
                    </a>
                  </div>
                  <div className={styles.positionApply}>
                    <a
                      href={position.apply_link}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <span className={`${styles.arrow} arrow`}>
                        <Image src="/arrow-black.svg" layout="fill" alt="" />
                      </span>
                      Apply
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div> */}

          <div className={styles.positionsOpenings}>
            <iframe
              src="https://app.trinethire.com/companies/341368-sakuu-corp/jobs/embed"
              style={{ width: '100%', height: '760px', border: '0' }}
            />
          </div>

          <h2
            className={styles.positionsSectionMoreTitle}
            dangerouslySetInnerHTML={createMarkup(
              props.originalResponse.currentopeningssection.more_title
            )}
          ></h2>
          <div
            className={styles.positionsSectionMoreDetail}
            dangerouslySetInnerHTML={createMarkup(
              props.originalResponse.currentopeningssection.more_detail
            )}
          ></div>
          {!hbsptForm && (
            <a
              className={styles.positionsSectionSignup}
              onClick={() => setHbsptForm(!hbsptForm)}
            >
              <span className={styles.positionsSectionSignupArrow}>
                <Image
                  className={styles.linkArrow}
                  src="/arrow-black.svg"
                  layout="fill"
                  alt=""
                />
              </span>
              Sign up for job alerts
            </a>
          )}
          {hbsptForm && <CareersSignup />}
        </section>
      </div>
    </div>
  )
}

export async function getStaticProps() {
  const page: GenericPageLayoutProps = await parseGenericPage(
    `${getAPI()}/careerspage/`
  )

  const jobOpeningRequest = await fetch(`${getAPI()}/job-openings/`)
  const jobOpenings = await jobOpeningRequest.json()

  return {
    props: {
      originalResponse: page.originalResponse,
      title: page.title,
      jobOpenings: jobOpenings,
      globalData: page.globalData,
      meta_title: page.meta_title,
      meta_description: page.meta_description,
    },
    revalidate: NEXTJS_REVALIDATE_PAGE_IN_SECONDS,
  }
}

export const NEXTJS_REVALIDATE_PAGE_IN_SECONDS = 10
export default Careers
